import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface QualityTableSkeletonProps {
  rows?: number;
  className?: string;
}

export function QualityTableSkeleton({ rows = 8, className }: QualityTableSkeletonProps) {
  return (
    <>
      {[...Array(rows)].map((_, index) => (
        <TableRow 
          key={index}
          className={cn(
            "opacity-0 animate-in fade-in-50",
            className
          )}
          style={{
            animationDelay: `${index * 50}ms`,
            animationFillMode: 'forwards',
            animationDuration: '300ms'
          }}
        >
          {/* Checkbox */}
          <TableCell className="w-[30px]">
            <Skeleton className="h-4 w-4" />
          </TableCell>
          
          {/* Measure ID */}
          <TableCell className="w-[100px]">
            <Skeleton className="h-4 w-16" />
          </TableCell>
          
          {/* Title */}
          <TableCell className="min-w-[200px]">
            <div className="space-y-2">
              <Skeleton className="h-4 w-full max-w-[300px]" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          </TableCell>
          
          {/* Submission Method */}
          <TableCell className="w-[120px]">
            <Skeleton className="h-6 w-20 rounded-md" />
          </TableCell>
          
          {/* Denominator */}
          <TableCell className="w-[100px] text-right">
            <Skeleton className="h-4 w-12 ml-auto" />
          </TableCell>
          
          {/* Data Completeness */}
          <TableCell className="w-[100px] text-right">
            <Skeleton className="h-4 w-10 ml-auto" />
          </TableCell>
          
          {/* Performance Rate */}
          <TableCell className="w-[100px] text-right">
            <Skeleton className="h-4 w-10 ml-auto" />
          </TableCell>
          
          {/* Benchmark Bar */}
          <TableCell className="w-[150px]">
            <Skeleton className="h-6 w-full rounded-sm" />
          </TableCell>
          
          {/* Points */}
          <TableCell className="w-[80px] text-right">
            <Skeleton className="h-6 w-12 ml-auto rounded-md" />
          </TableCell>
          
          {/* Actions */}
          <TableCell className="w-[50px] text-center">
            <Skeleton className="h-8 w-8 rounded-md mx-auto" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// Skeleton for the summary cards at the top of the quality page
export function QualitySummaryCardsSkeleton() {
  return (
    <>
      {/* Unified Score Card Skeleton */}
      <section className="mb-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <Skeleton className="h-3 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </div>
              <div className="h-10 w-px bg-border" />
              <div>
                <Skeleton className="h-3 w-32 mb-2" />
                <Skeleton className="h-7 w-20" />
              </div>
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
      </section>
      
      {/* Category Cards Skeleton */}
      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between space-x-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-5 w-5 rounded-full" />
          </div>
          <div className="mt-3 space-y-1">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </section>
    </>
  );
}
